import type { FC, ReactNode } from 'react'
import type { TileCell } from '@/components/landing/tiles'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { CountUp } from '@/components/generals/count-up'
import { SectionBadge } from '@/components/landing/section-badge'
import { TileField } from '@/components/landing/tiles'
import { useInViewTrigger } from '@/hooks/use-in-view-trigger'
import { useScrollProgress } from '@/hooks/use-scroll-progress'

import { art } from '@/constants/art'

// Hidden while waiting below the fold, then rises in once the stats scroll into view.
const STAGGER = 'group-data-[anim=armed]/stats:opacity-0 motion-safe:group-data-[anim=play]/stats:animate-rise'

const STATS = [
    { label: 'Repetitive work automated', value: '40%' },
    { label: 'Teams on board', value: '1,200+' },
    { label: 'Runs finished cleanly', value: '99.2%' },
    { label: 'Average setup time', value: '9 min' }
]

const TOP_LEFT: TileCell[] = [[1, 0], [3, 0], [4, 0], [0, 1], [2, 1], [3, 1], [5, 1], [1, 2]]
const TOP_RIGHT: TileCell[] = [[1, 0], [3, 0], [5, 0], [0, 1], [2, 1], [4, 1], [1, 2], [4, 2]]
const LOW_LEFT: TileCell[] = [[1, 0], [0, 1], [2, 1], [4, 1], [1, 2], [3, 2]]
const LOW_RIGHT: TileCell[] = [[3, 0], [0, 1], [2, 1], [4, 1], [1, 2], [3, 2]]

// Wordmarks for the partner strip. Swap in the real logo files once you have them.
const LOGOS: { name: string, mark: ReactNode }[] = [
    {
        name: 'Modulify',
        mark: (
            <span className='flex items-center gap-2'>
                <span className='font-heading text-[27px] leading-none font-bold tracking-[-0.04em] sm:text-[31px]'>modulify</span>
            </span>
        )
    },
    {
        name: 'Wedoflow',
        mark: (
            <span className='flex items-center gap-2'>
                <span className='font-heading text-[27px] leading-none font-medium tracking-[-0.03em] sm:text-[31px]'>wedoflow</span>
            </span>
        )
    },
    {
        name: 'Fontinlogo',
        mark: (
            <span className='flex items-center gap-2'>
                <span className='font-heading text-[27px] leading-none font-semibold italic tracking-[-0.03em] sm:text-[31px]'>Fontinlogo</span>
            </span>
        )
    },
    {
        name: 'Logotouse',
        mark: (
            <span className='flex items-center gap-2'>
                <span className='text-[24px] leading-none font-semibold tracking-[-0.02em] sm:text-[27px]'>Logotouse</span>
            </span>
        )
    }
]

export const ProofSection: FC = (): ReactNode => {
    const { tree } = art
    const [stageRef, stage] = useInViewTrigger<HTMLDivElement>()

    // The tree drifts at its own pace as the section scrolls past, rising behind the cards.
    useScrollProgress(stageRef, '--tree-progress', 'through')

    return (
        <section
            id='proof'
            aria-labelledby='proof-title'
            className='relative isolate overflow-hidden bg-white pt-24 pb-16 sm:pt-36'
        >
            <TileField cells={TOP_LEFT} size='76px' className='top-[-38px] left-[-24px] max-lg:hidden' />
            <TileField cells={TOP_RIGHT} size='76px' className='top-[-30px] right-[-30px] max-lg:hidden' />

            <Reveal className='relative z-20 mx-auto flex max-w-3xl flex-col items-center px-5 text-center'>
                <SectionBadge>Proven at work</SectionBadge>

                <h2
                    id='proof-title'
                    className='mt-6 font-heading text-[clamp(2.1rem,3.1vw,2.8rem)] leading-[1.18] tracking-[-0.03em] text-balance text-ink'
                >
                    Over 1.2 million workflows <br className='max-sm:hidden' />
                    handled without a hitch.
                </h2>
            </Reveal>

            <div
                ref={stageRef}
                data-anim={stage}
                className={cn(
                    'group/stats relative mx-auto mt-14 w-full max-w-[1100px] px-5 sm:mt-20 sm:px-8',
                    tree && 'sm:pb-[calc(min(72vw,540px)*0.7)]'
                )}
            >
                {tree && (
                    <div
                        aria-hidden
                        className='pointer-events-none absolute bottom-0 left-1/2 z-10 aspect-[3/4] w-[min(72vw,540px)] mix-blend-multiply group-data-[anim=armed]/stats:opacity-0 max-sm:hidden motion-safe:group-data-[anim=play]/stats:animate-grow-tree'
                        style={{
                            animationDelay: '250ms',
                            translate: '-50% calc((0.5 - var(--tree-progress, 0.5)) * 220px)'
                        }}
                    >
                        <Image
                            src={tree.src}
                            alt=''
                            fill
                            sizes='(min-width: 768px) 540px, 72vw'
                            className='fade-bottom object-contain object-bottom [--fade:10%]'
                        />
                    </div>
                )}

                <TileField cells={LOW_LEFT} size='56px' className='bottom-[4%] -left-[12%] max-xl:hidden' />
                <TileField cells={LOW_RIGHT} size='56px' className='-right-[12%] bottom-[10%] max-xl:hidden' />

                <ul className='grid grid-cols-2 gap-3.5 sm:gap-5 lg:grid-cols-4'>
                    {STATS.map((stat, index) => (
                        <li key={stat.label} className='relative min-h-[172px] sm:min-h-[210px] lg:min-h-[228px]'>
                            {/* Background and text animate separately so the tree can keep slipping between them */}
                            <div
                                aria-hidden
                                className={cn('ticket absolute inset-0 z-0 rounded-[28px] bg-meadow [--notch:17px] sm:rounded-[34px] sm:[--notch:22px]', STAGGER)}
                                style={{ animationDelay: `${index * 110}ms` }}
                            />

                            <div
                                className={cn('relative z-20 flex h-full flex-col justify-between gap-6 px-6 py-6 sm:px-9 sm:py-8', STAGGER)}
                                style={{ animationDelay: `${index * 110}ms` }}
                            >
                                <p className='text-[14px] leading-snug font-medium text-ink sm:text-[15px]'>
                                    {stat.label}
                                </p>

                                <p className='font-heading text-[clamp(2.3rem,4.3vw,3.65rem)] leading-none font-semibold tracking-[-0.035em] text-ink'>
                                    <CountUp value={stat.value} duration={1700} />
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>

                <div
                    className={cn('relative z-30 mt-5 overflow-hidden rounded-[30px] border border-white/90 bg-white/65 shadow-[0_24px_60px_-34px_rgba(20,23,15,0.45)] backdrop-blur-2xl sm:rounded-[34px]', STAGGER)}
                    style={{ animationDelay: '420ms' }}
                >
                    <ul className='flex flex-wrap items-stretch justify-center text-ink'>
                        {LOGOS.map((logo, index) => (
                            <li
                                key={logo.name}
                                className='flex h-[92px] basis-1/2 items-center justify-center px-4 lg:h-[124px] lg:flex-1 lg:basis-0 lg:border-l lg:border-black/[0.06] lg:first:border-l-0'
                            >
                                <span className='sr-only'>{logo.name}</span>
                                <span
                                    aria-hidden
                                    className={cn('inline-block', STAGGER)}
                                    style={{ animationDelay: `${560 + index * 90}ms` }}
                                >
                                    {logo.mark}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    )
}
